import type React from 'react';
import { useContext } from 'react';

import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';

import { ImportModalTypes } from '../Modals/ImportModalTypes.ts';
import { ProgressStatus } from '../Modals/ProgressModal/progress-types.ts';
import { isDocWithWrongType } from '../../utils/fileValidation.ts';
import { ImportStateContext } from '../ImportStateContextProvider.tsx';
import { getProcessErrorType } from '../../utils/getProcessErrorType.ts';
import type { DocumentsType, ImportStrategyType, UploadStrategyType } from '../types.ts';
import { useFileSizeCheck } from '../../hooks/useFileSizeCheck.ts';
import { useAddAbortControllerDocuments } from '../../hooks/useAddAbortControllerDocuments.ts';
import { REQUEST_ABORTED } from '../../utils/cfetchWithAbort.ts';
import { createParentPage } from '../../services/DocImportService.ts';
import { MarkdownImportError } from '../../services/MarkdownImportService.ts';

const isExpectedAbort = (error: unknown): boolean =>
	error === REQUEST_ABORTED || (error instanceof Error && Reflect.get(error, 'code') === 'aborted');

export const useUploadSingle = (
	handleUploadFileWithDiscard: any,
	setCreatedDraftId: any,
	setImportedDraftPageData: any,
	setElementProcessingError: any,
	importStrategy: React.MutableRefObject<React.MutableRefObject<ImportStrategyType> | null>,
	currentSpace: string,
	parentPageId: string | null,
	_restrictionType: string,
	subType: string | undefined,
	setImportedPageData: any,
	deleteOnboardingBlankPage?: () => Promise<void>,
	spaceId?: string | null,
	hasSourceRestrictions = false,
): UploadStrategyType => {
	const { showModal, closeModal, updateModalData } = useContext(ImportStateContext);
	const { isEnabled: isUploadInOnboardingEnabled, loading: isUploadInOnboardingLoading } =
		useTwcg2304ImportInOnboarding();
	const fileSizeCheck = useFileSizeCheck();

	const handleDocumentProcessingErrors = (errors) => {
		if (!errors) return;
		const processErrorType = getProcessErrorType(errors);
		setElementProcessingError(processErrorType);
	};

	const addAbortControllerDocuments = useAddAbortControllerDocuments();

	const isDocumentValid = (document: { type: string; size: number; name: string }): boolean => {
		if (isDocWithWrongType(document.type)) {
			showModal(ImportModalTypes.FILE_TYPE);
			return false;
		}

		return fileSizeCheck(document);
	};

	const validateDocumentAndCloseProgress = (document: {
		type: string;
		size: number;
		name: string;
	}): boolean => {
		if (!isDocumentValid(document)) {
			closeModal(ImportModalTypes.PROGRESS);
			return false;
		}

		return true;
	};

	const parseErrorCode = async (error) => {
		try {
			const errorBody = await error.response?.json();
			return errorBody?.error?.code;
			/* eslint-disable @typescript-eslint/no-unused-vars */
		} catch (e) {
			return;
		}
	};

	const handleCreateParentPage = async (): Promise<string | undefined> => {
		try {
			const folderName = importStrategy.current?.current?.folderName;
			const result = await createParentPage(
				currentSpace,
				parentPageId,
				'free',
				subType,
				folderName,
				true,
			);
			return result.data.id;
		} catch (err) {
			if (err === REQUEST_ABORTED) return;
			closeModal(ImportModalTypes.PROGRESS);
			showModal(ImportModalTypes.UPLOAD_ERROR);
		}
	};

	const handleUpload = async (documents: File[]) => {
		// #TODO Move abort signal merge hook to context to avoid duplications
		// https://jira.atlassian.com/browse/CONFDEV-82060
		const [documentWithAbortSignal] = addAbortControllerDocuments(documents);

		const { doc, signal }: { doc: File; signal: AbortSignal } = documentWithAbortSignal;

		try {
			const strategy = importStrategy.current?.current;
			const uploadSingle = strategy?.uploadSingle;
			if (uploadSingle) {
				if (hasSourceRestrictions) {
					throw new MarkdownImportError('invalid_destination');
				}

				if (!spaceId) {
					throw new Error('A numeric space ID is required for this import strategy');
				}

				const resolvedParentPageId = strategy.resolveParentPage
					? await strategy.resolveParentPage({
							spaceId,
							spaceKey: currentSpace,
							parentPageId,
							signal,
						})
					: parentPageId;

				const { data, errors, warnings } = await uploadSingle(doc, {
					spaceId,
					spaceKey: currentSpace,
					parentPageId: resolvedParentPageId,
					signal,
					onCreateDispatch: () =>
						updateModalData(ImportModalTypes.PROGRESS, { cancelDisabled: true }),
				});
				setCreatedDraftId(data.id);
				setImportedDraftPageData(data);
				handleDocumentProcessingErrors(errors);
				strategy.onImportCompleted?.({
					mode: 'single',
					result: 'success',
					selectedFileCount: documents.length,
					successfulFileCount: 1,
					failedFileCount: 0,
					warnings,
				});
				updateModalData(ImportModalTypes.PROGRESS, {
					status: ProgressStatus.SUCCESS,
					...(warnings?.length ? { warnings } : {}),
				});
				return;
			}

			const isExperimentActive = !isUploadInOnboardingLoading && isUploadInOnboardingEnabled;
			const createdParentPageId = isExperimentActive ? await handleCreateParentPage() : undefined;

			if (isExperimentActive && !createdParentPageId) return;

			if (isExperimentActive && createdParentPageId) {
				setImportedPageData({ id: createdParentPageId, space: currentSpace });
			}

			const uploadOptions: { signal: AbortSignal; createdBulkParentPageId?: string } = { signal };
			if (createdParentPageId) {
				uploadOptions.createdBulkParentPageId = createdParentPageId;
			}
			const { data, errors } = await handleUploadFileWithDiscard(doc, uploadOptions);

			setCreatedDraftId(data.id);
			setImportedDraftPageData(data);

			// When the experiment created a separate parent page for the import, the blank page
			// that was originally created (e.g. via handleImportCTAClick) is not replaced by the
			// normal discard mechanism. Delete it explicitly.
			if (isExperimentActive && createdParentPageId && deleteOnboardingBlankPage) {
				void deleteOnboardingBlankPage();
			}
			handleDocumentProcessingErrors(errors);
			updateModalData(ImportModalTypes.PROGRESS, {
				status: ProgressStatus.SUCCESS,
			});
		} catch (error) {
			const strategy = importStrategy.current?.current;
			closeModal(ImportModalTypes.PROGRESS);

			if (isExpectedAbort(error)) {
				strategy?.onImportCompleted?.({
					mode: 'single',
					result: 'cancelled',
					selectedFileCount: documents.length,
					successfulFileCount: 0,
					failedFileCount: 0,
					errorCode: 'aborted',
				});
				return;
			}

			if (error instanceof MarkdownImportError && error.code === 'outcome_unknown') {
				strategy?.onImportCompleted?.({
					mode: 'single',
					result: 'outcome_unknown',
					selectedFileCount: documents.length,
					successfulFileCount: 0,
					failedFileCount: 0,
					errorCode: 'outcome_unknown',
				});
				showModal(ImportModalTypes.OUTCOME_UNKNOWN, { attemptId: error.attemptId });
				return;
			}

			strategy?.onImportCompleted?.({
				mode: 'single',
				result: 'create_failure',
				selectedFileCount: documents.length,
				successfulFileCount: 0,
				failedFileCount: 1,
				errorCode: error instanceof MarkdownImportError ? error.code : 'unknown',
			});

			const errorCode = await parseErrorCode(error);
			if (ImportModalTypes[errorCode]) {
				showModal(ImportModalTypes[errorCode]);
			} else {
				showModal(ImportModalTypes.UPLOAD_ERROR);
			}
		}
	};

	const validateBeforeProgress = async (documents): Promise<DocumentsType | false> => {
		const importStrategyRef = importStrategy.current;

		if (importStrategyRef!.current.validateSingleUploadBeforeProgress) {
			return importStrategyRef!.current.validateSingleUploadBeforeProgress(documents);
		}

		const isValid = validateDocumentAndCloseProgress(documents[0]);
		return isValid ? documents : false;
	};

	const handleFilesBeforeUpload = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original strategy
		const importStrategyRef = importStrategy.current;
		if (importStrategyRef!.current.exportFile) {
			const exportedDoc = await importStrategyRef!.current.exportFile(documents[0], false);
			return exportedDoc ? [exportedDoc] : [];
		}

		return documents;
	};

	const validateBeforeUpload = async (documents) => {
		// don't use destructuring on the top of the file to keep the link to the original strategy
		const importStrategyRef = importStrategy.current;
		if (importStrategyRef!.current.validateSingleBeforeUpload) {
			return importStrategyRef!.current.validateSingleBeforeUpload(documents);
		}

		const filteredDocs = documents.filter((item) => !!item);

		return filteredDocs.length ? filteredDocs : false;
	};

	return {
		handleUpload,
		validateBeforeProgress,
		handleFilesBeforeUpload,
		validateBeforeUpload,
	};
};
